import React from "react";
import { Route, Provider } from "../common/ObjectTypes";
import { useTranslation } from "react-i18next";
import { getRouteStatus } from "./getRouteStatus";
import { routeDetailsClasses } from "../common/tailwindClassnames";
import { routeDisplayName } from "./routeDisplayName";

type RouteDetailsProps = {
    reloadRoute: boolean,
    route: Route,
    routeProvider: Provider,
    // Unsaved edit of the Route's own name (decoupled from the provider's) —
    // shown here so the header tracks what the General tab is typing.
    routeName?: string,
    subPage: string | undefined,
    onSelectSubPage: Function,
}

const RouteDetails = ({
    reloadRoute,
    route,
    routeProvider,
    routeName,
    subPage,
    onSelectSubPage,
}: RouteDetailsProps) => {
    const { t } = useTranslation();
    const routeStatus = getRouteStatus(route.status, true, t);

    return (
        <div className={routeDetailsClasses.container}>
            <div className={routeDetailsClasses.header}>
                <div className="max-w-list-table mx-auto">
                {/* page header — the Route's on/off switch lives on the flow map's
                    outgoing arrow, not here. */}
                <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                    <div className="flex flex-col md:w-3/4">
                        <div className="flex flex-row items-start">
                            <h1 className={routeDetailsClasses.title}>{routeName ?? routeDisplayName(route)}</h1>
                            {/* Disabled state is not repeated here — the flow map
                                header already carries the Route Disabled chip. */}
                            {(!reloadRoute && routeProvider.enabled) && <p className={`mx-3 mt-2 ${routeStatus.style}`}>{routeStatus.text}</p>}
                        </div>
                        <div className="flex flex-row items-center gap-2 mt-2 mb-3">
                            {route?.provider?.type?.name && <span className={routeDetailsClasses.headerPill}>{route.provider.type.name}</span>}
                            {route?.owner?.name && <span className={routeDetailsClasses.headerPill}>{route.owner.name}</span>}
                        </div>
                    </div>
                </div>
                {/* tabs for sub-pages — bidirectional redesign: technology-named tabs */}
                <div className="w-full flex flex-row font-semibold -ml-3" role="tablist">
                    {([
                        ["configurations", t("Configurations"), "RouteDetails-configurations-tab"],
                        ["sources", t("Sources"), undefined],
                        ["logs", t("Logs"), undefined],
                    ] as [string, string, string | undefined][]).map(([slug, label, testId]) => (
                        <label key={slug}
                            className={(subPage === slug ? routeDetailsClasses.tabActive : routeDetailsClasses.tabInactive).replace('mr-3', 'mx-3')}
                            data-testid={testId}
                            role="tab"
                            tabIndex={0}
                            aria-selected={subPage === slug}
                            onClick={() => onSelectSubPage(slug)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSelectSubPage(slug); } }}>
                            {label}
                        </label>
                    ))}
                </div>
                </div>
            </div>
        </div>
    );
};

export default RouteDetails;